import { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { format, subMonths, eachMonthOfInterval } from 'date-fns';

interface MissingInvoiceDataProps {
  dateFilter: string;
  utilityTypeFilter: string;
  locationFilter: string;
  vendorFilter: string;
}

interface MissingDataRow {
  location: string;
  billingId: string;
  vendor: string;
  months2025: string[];
  months2024: string[];
}

const generateMonths = () => {
  const endDate = new Date(2025, 11, 1);
  const startDate = subMonths(endDate, 12);
  return eachMonthOfInterval({ start: startDate, end: endDate }).reverse();
};

const generateDummyData = (): MissingDataRow[] => {
  const locations = [
    '1 Ashgrove Court',
    '10 Koehler Ave Apt. #9',
    '11001 Quailwood Drive',
    '11503 Springfield Pike',
    '14 Koehler Ave Apt. #7',
    '2183 Quailhollow Place',
  ];

  const vendors = [
    'City of Franklin Utility Department',
    'Duke Energy',
    'Greater Cincinnati Water Works',
    'Rumpke',
  ];

  return locations.flatMap((location, locIdx) => {
    return vendors.map((vendor, vendorIdx) => {
      const billingId = `${9101 + locIdx * 1000 + vendorIdx * 100} ${1805 + locIdx * 10} ${9572 + vendorIdx}`;
      
      // Generate months with some missing
      const months2025: string[] = [];
      const months2024: string[] = [];
      
      // 2025 months (some with asterisks indicating special status)
      const months = ['Oct', 'Sep', 'Aug', 'Jul', 'Jun', 'May', 'Apr', 'Mar'];
      months.forEach((month, idx) => {
        if (Math.random() > 0.2) { // 80% chance of having data
          months2025.push(Math.random() > 0.7 ? `${month}*` : month);
        }
      });

      return {
        location,
        billingId,
        vendor,
        months2025,
        months2024,
      };
    });
  });
};

export function MissingInvoiceData({
  dateFilter,
  utilityTypeFilter,
  locationFilter,
  vendorFilter,
}: MissingInvoiceDataProps) {
  const dummyData = useMemo(() => generateDummyData(), []);

  const filteredData = useMemo(() => {
    let filtered = [...dummyData];
    
    if (vendorFilter !== 'all') {
      filtered = filtered.filter(row => 
        row.vendor.toLowerCase().includes(vendorFilter.toLowerCase())
      );
    }

    return filtered;
  }, [dummyData, vendorFilter]);

  const months2025 = ['Oct', 'Sep', 'Aug', 'Jul', 'Jun', 'May', 'Apr', 'Mar', 'Feb', 'Jan'];
  const months2024 = ['Dec', 'Nov'];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Missing Invoice Data</CardTitle>
        <CardDescription>
          Locations and accounts with missing invoice data for specific months
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[200px]">Location</TableHead>
                <TableHead className="min-w-[180px]">Billing ID</TableHead>
                <TableHead className="min-w-[200px]">Vendor</TableHead>
                <TableHead className="min-w-[400px]">2025</TableHead>
                <TableHead className="min-w-[200px]">2024</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredData.map((row, idx) => (
                <TableRow key={idx}>
                  <TableCell className="font-medium">{row.location}</TableCell>
                  <TableCell>{row.billingId}</TableCell>
                  <TableCell>{row.vendor}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 flex-wrap">
                      {row.months2025.length > 0 ? (
                        row.months2025.map((month, monthIdx) => (
                          <Badge 
                            key={monthIdx} 
                            variant={month.includes('*') ? 'default' : 'secondary'}
                            className="text-xs"
                          >
                            {month}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-muted-foreground text-sm">No data</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {row.months2024.length > 0 ? (
                      <div className="flex items-center gap-1 flex-wrap">
                        {row.months2024.map((month, monthIdx) => (
                          <Badge key={monthIdx} variant="secondary" className="text-xs">
                            {month}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

